/* Standalone, full-screen chat page — same brain as the floating widget but
   takes the whole screen. Lets Scott share a clean deep-link with customers
   (scottsellschevys.com/chat) and works great on mobile. */
import { useEffect, useRef, useState } from "react";
import { Link as RLink, useLocation, useNavigate } from "react-router-dom";
import { Send, Loader2, Phone, ChevronLeft } from "lucide-react";
import { sendChat, chatbotBootstrap } from "../lib/api";
import { siteConfig } from "../config";

const FALLBACK_GREETING = "Hi, I'm Scott's virtual assistant. How can I help with your vehicle search today?";

function sessionId() {
  try {
    let s = localStorage.getItem("ssc_chat_session");
    if (!s) {
      s = "ssc_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("ssc_chat_session", s);
    }
    return s;
  } catch { return "ssc_" + Date.now().toString(36); }
}

function renderAssistant(text) {
  const out = [];
  const linkRE = /\[([^\]]+)\]\((\/[^\s)]+)\)/g;
  let last = 0; let m; let k = 0;
  while ((m = linkRE.exec(text)) !== null) {
    if (m.index > last) out.push(<span key={k++}>{text.slice(last, m.index)}</span>);
    out.push(<RLink key={k++} to={m[2]} className="text-blue-600 font-bold underline">{m[1]}</RLink>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<span key={k++}>{text.slice(last)}</span>);
  return out;
}

export default function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([{ role: "assistant", content: FALLBACK_GREETING }]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [intent, setIntent] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");
  const scrollRef = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => {
    chatbotBootstrap("/chat").then((b) => {
      if (b?.starter) setMessages([{ role: "assistant", content: b.starter }]);
      if (Array.isArray(b?.quick_replies)) setQuickReplies(b.quick_replies);
      if (b?.disclaimer) setDisclaimer(b.disclaimer);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async (textRaw) => {
    const text = (textRaw || "").trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setShowQuickReplies(false);
    setBusy(true);
    try {
      const r = await sendChat({
        session_id: sid.current, message: text, history: messages,
        page_path: "/chat", referrer: document.referrer || null,
      });
      if (r.ok) {
        setMessages([...next, { role: "assistant", content: r.reply }]);
        setIntent(r.intent);
        if (r.suggest_handoff) {
          setTimeout(() => setMessages((arr) => [...arr, {
            role: "assistant",
            content: "Want Scott to reach out with real numbers and availability? Tap the contact button below — or text him at " + siteConfig.scott.phone + ".",
          }]), 700);
        }
      } else {
        setMessages([...next, { role: "assistant", content: r.error || `Connection hiccup — text Scott at ${siteConfig.scott.phone}.` }]);
      }
    } catch {
      setMessages([...next, { role: "assistant", content: `Network hiccup — text Scott at ${siteConfig.scott.phone}.` }]);
    } finally { setBusy(false); }
  };

  const send = (e) => { e?.preventDefault?.(); const t = draft.trim(); if (!t) return; setDraft(""); sendMessage(t); };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white" data-testid="chat-page">
      {/* Header */}
      <header className="bg-gradient-to-b from-[#061426] to-[#0a1f3a] text-white sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1 hover:bg-white/10 rounded" aria-label="Back" data-testid="chat-page-back">
            <ChevronLeft size={20} />
          </button>
          <div className="w-10 h-10 rounded-full bg-[var(--ssc-blue,#006DFF)] flex items-center justify-center font-extrabold text-white text-sm">SS</div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-base leading-tight">Scott's Virtual Assistant</div>
            <div className="text-[11px] text-blue-200/80">Car-shopping help · instant replies</div>
          </div>
          <a href={siteConfig.scott.phoneSms} className="text-xs bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1" data-testid="chat-page-call-scott">
            <Phone size={12} /> Text Scott
          </a>
        </div>
      </header>

      {disclaimer && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-[11px] px-4 py-2 text-center" data-testid="chat-page-disclaimer">
          <span className="font-bold">Heads-up:</span> {disclaimer}
        </div>
      )}

      {/* Messages */}
      <main ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="chat-page-body">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed max-w-[85%] ${
                m.role === "user"
                  ? "bg-[var(--ssc-blue,#006DFF)] text-white rounded-br-sm"
                  : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm"
              }`}>
                {m.role === "assistant" ? renderAssistant(m.content) : m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-3.5 py-2 text-sm bg-white border border-gray-200 text-gray-500 inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> typing…
              </div>
            </div>
          )}
          {showQuickReplies && quickReplies.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-page-quick-replies">
              {quickReplies.map((qr) => (
                <button key={qr.key} onClick={() => sendMessage(qr.starter || qr.label)} disabled={busy}
                  className="text-xs bg-white hover:bg-blue-50 border border-blue-200 text-blue-700 font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-50"
                  data-testid={`chat-page-qr-${qr.key}`}>
                  {qr.label}
                </button>
              ))}
            </div>
          )}
          {intent?.intent_class === "high_intent_lead" && (
            <RLink to="/contact" className="block w-full bg-gradient-to-r from-amber-500 to-red-500 text-white text-center text-sm font-bold rounded-lg px-3 py-3 hover:brightness-110 transition" data-testid="chat-page-hot-handoff">
              🔥 Connect with Scott now →
            </RLink>
          )}
        </div>
      </main>

      {/* Input */}
      <form onSubmit={send} className="border-t border-gray-200 bg-white sticky bottom-0">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-end gap-2">
          <input
            type="text" value={draft} onChange={(e) => setDraft(e.target.value)} disabled={busy}
            placeholder="Ask anything — vehicles, trades, payments, or to reach Scott"
            className="flex-1 border border-gray-300 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            data-testid="chat-page-input" />
          <button type="submit" disabled={busy || !draft.trim()} className="bg-[var(--ssc-blue,#006DFF)] hover:bg-blue-700 disabled:opacity-50 text-white rounded-full w-11 h-11 flex items-center justify-center" data-testid="chat-page-send" aria-label="Send">
            <Send size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}
